import { clsx } from 'clsx';
import { EngineContext } from 'engine/components/EngineProvider';
import { FEEDBACK_URL } from 'game/config';
import { PlantEntity } from 'game/entities/PlantEntity';
import { ArrowIcon } from 'game/icons/ArrowIcon';
import { createStyles } from 'game/lib/createStyles';
import { usePersistentStorage } from 'game/lib/persistentStorage';
import { colors, fontWeight, fonts } from 'game/styles/theme';
import { memo, useContext, useState } from 'react';
import { Attribution } from './Attribution';
import { prefsStorage } from './prefs';

const classes = createStyles({
  debug: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    fontSize: 16,
    fontFamily: fonts.nunito,
  },
  heading: {
    color: colors.black,
    font: 'inherit',
    fontWeight: fontWeight.bold,
    textTransform: 'uppercase',
    opacity: 0.6,
    padding: 10,
    display: 'flex',
    alignItems: 'center',
  },
  arrow: {
    marginLeft: 4,
  },
  arrowInverted: {
    transform: 'rotate(180deg)',
  },
  list: {
    color: colors.grey14,
    background: 'rgba(0, 0, 0, 0.6)',
    padding: 10,
    '& > * + *': {
      marginTop: 6,
    },
    '& button': {
      color: 'inherit',
      font: 'inherit',
      border: `1px solid ${colors.grey14}`,
      padding: '2px 4px',
    },
    '& a': {
      color: 'inherit',
    },
  },
});

export const DebugMenu: React.FC = memo(() => {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<'attribution' | null>(null);

  const engine = useContext(EngineContext);
  const resetPlants = () => {
    engine.entities.forEach(e => {
      if (e instanceof PlantEntity) {
        e.props.waterAmount = 0;
      }
    });
  };

  const [prefs, setPrefs] = usePersistentStorage(prefsStorage);
  const { disableHelp, showColliders, skipIntro } = prefs;

  return (
    <>
      <div className={classes.debug}>
        <button type="button" className={classes.heading} onClick={() => setOpen(!open)}>
          Menu
          <ArrowIcon
            width={11}
            height={7}
            className={clsx(classes.arrow, {
              [classes.arrowInverted]: !open,
            })}
          />
        </button>
        {open && (
          <div className={classes.list}>
            <div>
              <button type="button" onClick={resetPlants}>
                Reset plants
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => setPrefs({ ...prefs, disableHelp: !disableHelp })}
              >
                Help messages: {disableHelp ? 'OFF' : 'ON'}
              </button>
            </div>
            <div>
              <button type="button" onClick={() => setPrefs({ ...prefs, skipIntro: !skipIntro })}>
                Skip intro: {skipIntro ? 'ON' : 'OFF'}
              </button>
            </div>
            <div>
              <button type="button" onClick={() => setPrefs({ ...prefs, showColliders: !showColliders })}>
                Show colliders: {showColliders ? 'ON' : 'OFF'}
              </button>
            </div>
            <div>
              <button type="button" onClick={() => setModal('attribution')}>
                Attribution
              </button>
            </div>
            <div>
              <a href={FEEDBACK_URL} target="_blank" rel="noreferrer">
                Send feedback
              </a>
            </div>
          </div>
        )}
      </div>
      {modal === 'attribution' && (
        <Attribution onClose={() => setModal(null)} />
      )}
    </>
  );
});
